import { computed, inject, Injector, Type } from '@angular/core';

import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { filter, pipe, switchMap, take, tap } from 'rxjs';

import environment from '@environments/environment';
import { CONTACT_DETAIL_STATE } from '@data-access/profiles/constants/contact.constant';
import { ContactService } from '@data-access/profiles/services/contact.service';
import { ContactDetail, ContactSignalState } from '@data-access/profiles/types';
import { withDialogFeatures } from '@data-access/shared/store/features/with-dialog-features.store';
import { tapResponse } from '@ngrx/operators';

import { OpenDialogState } from '@data-access/shared';

import {
  DIALOG_MODES,
  ADDRESS_TYPES_ENUM,
  Address,
  AddressNew,
  withCountryEntities,
} from '@erm/cluster';

import {
  AddToList,
  DragDropropData,
  ProfileListField,
  UpdateContactPayload,
} from '@data-access/profiles/types/contact.type';
import { GroupFieldsDialogData } from '@data-access/shared/types/dialog.type';

import {
  withAddressFeature,
  AddressDialogMetaData,
} from '@data-access/organizations';
import { AdminService } from '@admin/admin.service';
import { Role } from '@data-access/roles';
import { convertKeysToCamelCase } from '@shared/utils';
import { withDegreesEntities } from '../features/with-degrees.entities';
import { withSpecialtiesEntities } from '../features/with-specialties.entities';

export const CONTACT_DETAIL_SIGNAL_STORE = signalStore(
  withState(() => inject(CONTACT_DETAIL_STATE)),
  withProps((store, _contactService = inject(ContactService)) => ({
    _contactService,
    _adminService: inject(AdminService),
    _injector: inject(Injector),
    store,
  })),
  withDialogFeatures(),
  withFeature(({ _injector }) => withDegreesEntities(_injector)),
  withFeature(({ _injector }) => withSpecialtiesEntities(_injector)),
  withFeature(({ _injector }) =>
    withCountryEntities(environment.irisApi, _injector),
  ),
  withFeature(({ _contactService, openDialog }) =>
    withAddressFeature(_contactService, openDialog),
  ),
  withComputed(
    ({
      _degreesEntities,
      _specialtiesEntities,
      _countriesEntities,
      _statesEntities,
      _addressEntities,
      countriesTotal,
      statesTotal,
      data,
      error,
      loading,
      roles,
      userAttributes,
      canCloseDialog,
    }) => ({
      $addresses: computed(() => {
        const _addresses = _addressEntities();
        if (!_addresses.length) {
          return [];
        }
        return _addresses;
      }),
      $canCloseDialog: computed(() => canCloseDialog()),
      $contactId: computed(() => {
        const entity = data();

        if (!entity) {
          return '';
        }

        return entity.contactId;
      }),
      $countries: computed(() => {
        const data = _countriesEntities();
        if (!data) {
          return {
            data: [],
            total: 0,
          };
        }
        return {
          data: data,
          total: countriesTotal(),
        };
      }),
      $degrees: computed(() => {
        const data = _degreesEntities?.();
        const updatedData = data?.map((degree: string) => ({
          id: degree,
          label: degree,
        }));
        return [...updatedData];
      }),
      $data: computed(() => {
        const entity = data();
        const addresses = _addressEntities();

        if (Object.keys(entity ?? {}).length === 0) {
          return {} as ContactDetail;
        }

        // Sync addresses from entities back to data
        return {
          ...entity,
          mailingAddresses: addresses as unknown as AddressNew[],
        } as unknown as ContactDetail;
      }),
      $error: computed(() => error()),
      $isLoading: computed(() => loading()),
      $specialties: computed(() => {
        const data = _specialtiesEntities?.();
        const updatedData = data?.map((specialty: string) => ({
          id: specialty,
          label: specialty,
        }));
        return [...updatedData];
      }),
      $userId: computed(() => {
        const entity = data();

        if (!entity) {
          return '';
        }

        return entity.userId;
      }),
      $states: computed(() => {
        const data = _statesEntities();
        if (!data) {
          return {
            data: [],
            total: statesTotal(),
          };
        }
        return {
          data: data,
          total: statesTotal(),
        };
      }),
      $roles: computed(() => {
        const _roles = roles();
        return _roles;
      }),
      $userAttributes: computed(() => {
        const _attributes = userAttributes();
        return _attributes;
      }),
    }),
  ),
  withMethods(
    ({
      data,
      _injector,
      _contactService,
      _adminService,
      _matDialog,
      ...store
    }) => {
      const _payloads = () => {
        const _entity = data();
        const mailingAddresses = _entity?.mailingAddresses.map((address) => ({
          ...address,
          countryId: address.country.id ?? '',
        })) as Address[];
        return {
          additionalLegacyIds: _entity?.additionalLegacyIds ?? '',
          contactId: store.$contactId(),
          degrees: _entity?.degrees ?? [],
          legacyId: _entity?.legacyId ?? null,
          mailingAddresses: mailingAddresses ?? [],
          npi: _entity?.npi ?? null,
          phones: _entity?.phones ?? [],
          specialty: _entity?.specialty ?? [],
        };
      };
      const _loadContact = rxMethod<string>(
        pipe(
          tap(() => {
            patchState(store, { loading: true, error: null });
          }),
          switchMap((contactId) =>
            _contactService.loadContact(contactId).pipe(
              filter((contact): contact is ContactDetail => Boolean(contact)),
              tapResponse({
                error: (error: Error) => patchState(store, { error }),
                finalize: () => patchState(store, { loading: false }),
                next: (response) => {
                  const contactData = convertKeysToCamelCase(
                    response,
                  ) as ContactDetail;
                  patchState(store, {
                    data: contactData,
                  });
                  store.setAddresses(
                    contactData.mailingAddresses as unknown as AddressNew[],
                  );
                },
              }),
            ),
          ),
        ),
      );

      const _updateContact = rxMethod<{
        userId: string;
        payload: UpdateContactPayload;
      }>(
        pipe(
          switchMap(({ userId, payload }) =>
            _contactService.updateContact(userId, payload).pipe(
              filter(Boolean),
              tapResponse({
                error: (error: Error) =>
                  patchState(store, { error: error, loading: false }),
                finalize: () => patchState(store, { loading: false }),
                next: (response: ContactDetail) => {
                  const formattedData = convertKeysToCamelCase(
                    response,
                  ) as ContactDetail;
                  patchState(store, {
                    canCloseDialog: true,
                    data: { ...data(), ...formattedData },
                  });
                },
              }),
            ),
          ),
        ),
      );

      const _updateContactField = (updates: Partial<UpdateContactPayload>) => {
        const payload = {
          ..._payloads(),
          ...updates,
        };
        _updateContact({
          payload,
          userId: store.$userId(),
        });
      };

      const _removeFromListAtIndex = (
        field: ProfileListField,
        index: number,
      ) => {
        const currentItems = data()[field];
        const updatedItems = (currentItems as string[]).filter(
          (_, i) => i !== index,
        );
        _updateList(field, updatedItems);
      };

      const _addToList = (field: ProfileListField, item: string) => {
        const currentItems = data()[field] ?? [];
        if (
          Array.isArray(currentItems) &&
          currentItems.every((i) => typeof i === 'string')
        ) {
          const stringItems = currentItems as string[];
          if (!stringItems.includes(item)) {
            _updateList(field, [...stringItems, item]);
          }
        }
      };
      const _updateList = (
        field: ProfileListField,
        items: string[] | Address[],
      ) => {
        _updateContactField({ [field]: items });
      };
      const _updateListAtIndex = (
        field: ProfileListField,
        index: number,
        newItem: string | Address,
      ) => {
        const currentItems = _payloads()[field];

        if (field === 'mailingAddresses') {
          const updatedItems = [...(currentItems as Address[])];
          updatedItems[index] = newItem as Address;
          _updateList(field, updatedItems);
        } else {
          const updatedItems = [...(currentItems as string[])];
          updatedItems[index] = newItem as string;
          _updateList(field, updatedItems);
        }
      };

      const _moveInList = (data: DragDropropData) => {
        if (data.field === 'mailingAddresses') {
          _updateList(data.field, data.updatedItems as Address[]);
        } else {
          _updateList(data.field, data.updatedItems as string[]);
        }
      };

      const _openDialog = (payload: OpenDialogState) => {
        patchState(store, { canCloseDialog: false });
        const _dialogRef = store.openDialog(payload);

        if (_dialogRef) {
          _dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe({
              error: (err: unknown) => {
                console.error('Error handling dialog result:', err);
              },
              next: (dialogData) => {
                // Handle groupFields dialog
                if (
                  payload.type === 'groupFields' &&
                  dialogData &&
                  typeof dialogData === 'object'
                ) {
                  const group = (payload.componentData as GroupFieldsDialogData)
                    .group;
                  const updates: Record<string, any> = {};
                  for (const field of group.fields) {
                    if (field.propKey && field.id in dialogData) {
                      let value = dialogData[field.id];
                      if (!value && field.type === 'number') {
                        value = null;
                      }
                      // Keeping an exception for specialty
                      updates[field.propKey] =
                        field.propKey.toLowerCase() === 'specialty'
                          ? [value]
                          : value;
                    }
                  }
                  if (Object.keys(updates).length > 0) {
                    _updateContactField(updates);
                  }
                  return;
                }
                // Default handling for other dialog types
                if (!dialogData?.propKey) {
                  return;
                }

                switch (dialogData?.mode) {
                  case DIALOG_MODES.CREATE:
                    _addToList(dialogData.propKey, dialogData.value);
                    break;
                  case DIALOG_MODES.EDIT:
                    if (typeof dialogData.index === 'number') {
                      _updateListAtIndex(
                        dialogData.propKey,
                        dialogData.index,
                        dialogData.value,
                      );
                    } else {
                      console.warn(
                        'Index is undefined for EDIT mode:',
                        dialogData,
                      );
                    }
                    break;

                  case DIALOG_MODES.DELETE:
                    if (dialogData?.index !== undefined) {
                      _removeFromListAtIndex(
                        dialogData.propKey,
                        dialogData.index,
                      );
                    }
                    break;

                  default:
                    console.warn('Unhandled dialog mode:', dialogData.mode);
                    return;
                }
              },
            });
        }
      };
      /**
       * @NOTE: Need to explicitly load user roles and user_attributes here
       */
      const _loadRoles = rxMethod<void>(
        pipe(
          switchMap(() =>
            _adminService.getAllInternalRoles().pipe(
              tapResponse({
                error: (error: Error) => patchState(store, { error }),
                next: (roles) => {
                  const _cRoles = convertKeysToCamelCase(
                    roles.data,
                  ) as unknown as Role[];
                  patchState(store, {
                    roles: { data: _cRoles, total: roles.total },
                  });
                },
              }),
            ),
          ),
        ),
      );
      const _loadUserAttributes = rxMethod<void>(
        pipe(
          switchMap(() =>
            _adminService.getAttributeList().pipe(
              tapResponse({
                error: (error: Error) => patchState(store, { error }),
                next: (attributes) =>
                  patchState(store, { userAttributes: attributes }),
              }),
            ),
          ),
        ),
      );

      /**
       * @NOTE: End of user roles loading
       */

      return {
        addToList: (data: AddToList) => _addToList(data.propKey, data.value),
        clearContact: () => patchState(store, { data: {} as ContactDetail }),
        loadContact: _loadContact,
        loadCountries: () =>
          store.setCountries({ pageIndex: 1, pageSize: 300 }),
        loadDegrees: () => store.setDegrees(),
        loadSpecialties: () => store.setSpecialties(),
        loadStates: (countryId: string) => store.setStates({ countryId }),
        loadRoles: () => _loadRoles(),
        loadUserAttributes: () => _loadUserAttributes(),
        updateContactField: _updateContactField,
        moveInList: (data: DragDropropData) => _moveInList(data),
        openAddressDialog: (dialogMetaData: AddressDialogMetaData) =>
          store.loadAddressDialog({
            ...dialogMetaData,
            addressType: ADDRESS_TYPES_ENUM.home,
            entityId: data().userId,
          }),
        openSingleTextDialog: (payload: OpenDialogState) =>
          _openDialog(payload),
        setAddresses: (addresses: AddressNew[]) =>
          store.setAddresses(addresses),
      };
    },
  ),
) satisfies Type<ContactSignalState>;

export type ContactStore = InstanceType<typeof CONTACT_DETAIL_SIGNAL_STORE>;
